import React, { Component, PropTypes } from 'react';
import { get, debounce } from 'lodash';
import classNames from 'classnames';
import { connect } from 'react-redux';
import { PopoverEx as SparkPopover } from '../../ui/sabre-spark-react-ex/index';
import './FontConfigurator.scss';
import '../../../styles/styles.scss';
import FontConfiguratorRow from './FontConfiguratorRowInContext';
import { FontFamilyProperty } from '../../../../lib/css-property-parser/FontFamilyProperty';
import { checkPermissionForLevel } from '../../../../lib/utils';
import { ACCESS_POINTS_GROUP, ACCESS_POINT_LEVEL } from '../../../constants/AccessPoints';
import { actions } from '../../../store/InContextThemeStore';

class FontConfigurator extends Component {
    static propTypes = {
        fontStyleProperty: PropTypes.string,
        fontFamilyProperty: PropTypes.string.isRequired,
        fontWeightProperty: PropTypes.string,
        prependHook: PropTypes.node,
        fontFamilyLabel: PropTypes.string,
        fontFamilyClass: PropTypes.string,
        fontStyleClass: PropTypes.string,
        fontWeightClass: PropTypes.string,
        className: PropTypes.string
    };
    static defaultProps = {
        fontStyleProperty: '',
        fontFamilyProperty: '',
        fontWeightProperty: '',
        prependHook: null,
        fontFamilyLabel: 'Font Family',
        fontFamilyClass: 'col-xs-12 col-sm-3',
        fontWeightClass: 'col-xs-12 col-sm-3',
        fontStyleClass: 'col-xs-12 col-sm-3',
        className: ''
    };
    constructor(props) {
        super(props);
        const fontValue = props.font || props.value;
        this.state = {
            fontFamilies: new FontFamilyProperty(fontValue),
            manualFontInput: fontValue,
            fontSearch: '',
            addOtherFont: '',
            nestedPopovers: []
        };
        this.state.maxFonts = this.state.fontFamilies.value.length;
        this.handleRemoveFont= this.handleRemoveFont.bind(this);
        this._handleChange = debounce(this._handleChange.bind(this), 200);
    }
    componentWillReceiveProps(nextProps) {
        const currentFontValue = this.props.font || this.props.value;
        const nextFontValue = nextProps.font || nextProps.value;
        
        if (currentFontValue !== nextFontValue) {
            const nextFontFamilies = new FontFamilyProperty(nextFontValue);
            this.setState({
                fontFamilies: nextFontFamilies,
                manualFontInput: nextFontValue,
                maxFonts: Math.max(nextFontFamilies.value.length, 2)
            });
        }
    }
    _handleChange(newValue) {
        // Use the in-context theme action to update the font property
        this.props.onFontPropertyChange(this.props.fontFamilyProperty)(newValue);
        if (this.popoverRef) {
            this.popoverRef._sparkInstance.update();
        }
    }
    onManualChange = value => {
        const updatedFontFamilies = new FontFamilyProperty(value.target.value);
        this.setState(
            {
                manualFontInput: value.target.value,
                fontFamilies: updatedFontFamilies,
                maxFonts: Math.max(updatedFontFamilies.value.length, 2)
            },
            () => {
                this._handleChange(value.target.value);
            }
        );
    };
    onChange(index, value) {
        this.setState(
            prevState => {
                prevState.fontFamilies.value.splice(index, 1, value);
                return { fontFamilies: prevState.fontFamilies,
                    manualFontInput: prevState.fontFamilies.build()
                };
            },
            () => {
                this._handleChange(this.state.fontFamilies.build());
                this.props.onFontPropertyChange(this.props.fontFamilyProperty)(this.state.fontFamilies.build());
            }
        );
    }
    nestedPopoverHandler = (key, node) => {
        this.state.nestedPopovers[key] = node;
    };
    handleRemoveFont(rowProps) {
        if (rowProps.title === 'Default') {
            return;
        }
        const fontToRemove = rowProps.value;
        if (!fontToRemove) return;

        this.setState(prevState => {
            const currentFonts = [...prevState.fontFamilies.value];
            const normalizedFontToRemove = FontFamilyProperty.normalize(fontToRemove);
            const indexToRemove = currentFonts.findIndex(font => 
                FontFamilyProperty.normalize(font) === normalizedFontToRemove
            );
            
            if (indexToRemove > -1) {
                currentFonts.splice(indexToRemove, 1);
            }
            
            const rebuiltFontString = currentFonts.map(font => FontFamilyProperty.denormalize(font)).join(', ');
            const updatedFontFamilies = new FontFamilyProperty(rebuiltFontString);
            const builtValue = updatedFontFamilies.build();
            
            return {
                fontFamilies: updatedFontFamilies,
                manualFontInput: builtValue,
                maxFonts: Math.max(updatedFontFamilies.value.length, 2)
            };
        }, () => {
            this._handleChange(this.state.fontFamilies.build());
        });
    }
    renderRows(fontFamilies, handleRemoveFont) {
        const result = [];
        for (let i = 0; i < this.state.maxFonts; i++) {
            result.push(
                <FontConfiguratorRow
                    key={i}
                    value={fontFamilies[i] ? fontFamilies[i] : null}
                    title={i > 0 ? `Fallback font ${i}` : 'Default'}
                    nestedPopoverHandler={node => this.nestedPopoverHandler(i, node)}
                    onChange={value => this.onChange(i, value)}
                    handleRemoveFont={handleRemoveFont}
                />
            );
        }
        return result;
    }

    render() {
        const choosenFontFamilies = this.state.fontFamilies.value;
        const errorMessage = !FontFamilyProperty.isValid(this.state.manualFontInput) && 'Invalid font name.  Use single quotation marks for each font-family with more than one word.';
        return (
            <div className={classNames('row', 'font-configurator', this.props.className)}>
                {this.props.prependHook}
                <div className={this.props.fontFamilyClass}>
                    <SparkPopover
                        ref={node => (this.popoverRef = node)}
                        isDisabled={!this.props.permissionEdit}
                        anchorX="right"
                        className="col-xs-4"
                        nestedPopovers={this.state.nestedPopovers}
                        toggleEl={
                            <label
                                className={
                                    !this.props.permissionEdit
                                        ? 'spark-select has-value disabled'
                                        : 'spark-select has-value'
                                }
                            >
                                <span className="spark-label">{this.props.fontFamilyLabel}</span>
                            </label>
                        }
                    >
                        <div className={'row'}>
                            <div className={'col-xs-12'}>
                                <strong>Enter CSS or select your fonts</strong>
                            </div>
                        </div>
                        <div className={'row'}>
                            <div className={'col-xs-12'}>
                                <input
                                    value={this.state.manualFontInput}
                                    disabled={!this.props.permissionEdit}
                                    onChange={this.onManualChange}
                                    className="spark-input__field spark-input "
                                    // sparkInputProps={{ errorMessage }}
                                />
                                <span className="input-hint">Enter fonts comma delimited, with single quotation marks for each font-family with more than one word., e.g., Helvetica, 'Helvetica Neue'</span>
                            </div>
                        </div>
                        {this.renderRows(choosenFontFamilies, this.handleRemoveFont)}
                        <div className={'row spark-pad-t-1 spark-pad-b-1'}>
                            <div
                                style={{
                                    fontSize: '1.5rem',
                                    color: '#23826F'
                                }}
                                className={'col-xs-12 cursor-pointer'}
                                onClick={() => {
                                    this.setState((prevState, props) => {
                                        return { maxFonts: prevState.maxFonts + 1 };
                                    });
                                }}
                            >
                                + Add another fallback font
                            </div>
                        </div>
                    </SparkPopover>
                </div>
                {this.props.children}
            </div>
        );
    }
}

const mapStateToProps = (state, ownProps) => {
    return {
        availableFontFamilies: state.theme.fonts,
        currentTheme: state.app.data.theme,
        permissionEdit: checkPermissionForLevel(
            state.oauth.accessLevel,
            ACCESS_POINTS_GROUP.THEMES,
            ACCESS_POINT_LEVEL.EDIT,
            state.oauth.userPermissionLevels,
            state.app.selected.level
        ),
        font: get(state, `app.data.inContextTheme.light.${ownProps['fontFamilyProperty']}`),
        weight: get(state, `app.data.inContextTheme.light.${ownProps['fontWeightProperty']}`),
        style: get(state, `app.data.inContextTheme.light.${ownProps['fontStyleProperty']}`)
    };
};

const mapDispatchToProps = dispatch => {
    return {
        onFontPropertyChange: (property) => (value) => {
            dispatch(actions.updateLightTheme(property, value));
        }
    };
};

export default connect(mapStateToProps, mapDispatchToProps)(FontConfigurator);



// WEBPACK FOOTER //
// ./src/components/widgets/FontConfigurator/FontConfiguratorInContext.js